import { FC, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';

import { useSession } from '@eneco/auth';
import { ChatArgs, ChatContext, ChatContextValue, Namespace } from '@eneco/components/chat/useChat';
import { useRouter } from '@eneco/next';
import { useMachine } from '@xstate/react';

import chatMachine, { ChatEvent } from './ChatMachine';
import * as Seamly from './Seamly';
import { useScript } from './useScript';

export type SeamlyConfig = {
  defaultNamespace: Namespace;
  defaultTopic: string;
  instance?: string;
  defaultLocale?: string;
  userLocale?: string;
  accountId?: string;
  authToken?: string;
};

type Props = {
  children: ReactNode;
  chatSrc?: string;
  config?: SeamlyConfig;
};

export const ChatProvider: FC<Props> = ({ children, chatSrc, config }) => {
  const [state, send] = useMachine(chatMachine);
  const [script, setScript] = useState('');
  const chatState = typeof state.value === 'object' ? Object.values(state.value) : state.value;
  const router = useRouter();
  const { data: session } = useSession();

  // TODO This is where can handle Conversational CDN outages
  const [_loaded, _error] = useScript(script, Seamly.srcId, { async: false, defer: true });

  const sendEvent = useCallback(
    ({
      api,
      askText,
      context,
      layoutMode = 'window',
      namespace = config?.defaultNamespace ?? 'consumer',
      parentElement,
      topic = config?.defaultTopic,
      sendTopicAndAskText,
      variables,
      visibility = 'minimized',
      accountId = config?.accountId,
      authToken = session?.accessToken,
    }: ChatArgs = {}) => {
      if (config?.defaultLocale) {
        context = {
          ...context,
          contentLocale: config.defaultLocale,
          userLocale: config.userLocale,
        };
      }

      let type = `${layoutMode}:${visibility}`;

      if (sendTopicAndAskText) {
        type += ':copilot';
      } else if (accountId !== undefined && authToken !== undefined) {
        type += ':authenticated';
      }

      if (accountId && authToken) {
        context = {
          ...context,
          variables: {
            accountId: accountId,
            token: authToken,
          },
        };
      }

      send({
        api,
        askText,
        context,
        instance: config?.instance,
        namespace,
        parentElement,
        topic,
        type: type,
        accountId,
        authToken,
        variables,
      } as ChatEvent);
    },
    [
      config?.accountId,
      config?.defaultLocale,
      config?.defaultNamespace,
      config?.defaultTopic,
      config?.instance,
      config?.userLocale,
      session?.accessToken,
      send,
    ],
  );

  const mount = useCallback(sendEvent, [sendEvent]);
  const value = useMemo<ChatContextValue>(() => [mount], [mount]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (chatState?.includes('open')) {
        sendEvent({ visibility: 'minimized' });
      }
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should act as mount/unmount useEffect only
  }, []);

  useEffect(() => {
    if (chatState !== 'idle') {
      setScript(chatSrc ?? Seamly.src);
    }
  }, [chatSrc, chatState]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
